export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambient: { stop: () => void } | null = null;
  muted = false;

  private ensure(): AudioContext {
    if (!this.ctx) {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.7;
      this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  async resume(): Promise<void> {
    const ctx = this.ensure();
    if (ctx.state !== "running") await ctx.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : 0.7;
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  private out(): AudioNode {
    this.ensure();
    return this.master!;
  }

  private env(gain: GainNode, t: number, peak: number, attack: number, release: number): void {
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + attack + release);
  }

  ui(): void {
    const ctx = this.ensure();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(1320, t + 0.06);
    this.env(gain, t, 0.05, 0.005, 0.07);
    osc.connect(gain).connect(this.out());
    osc.start(t);
    osc.stop(t + 0.1);
  }

  laser(): void {
    const ctx = this.ensure();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(1480, t);
    osc.frequency.exponentialRampToValueAtTime(180, t + 0.12);
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(2400, t);
    this.env(gain, t, 0.12, 0.004, 0.12);
    osc.connect(filter).connect(gain).connect(this.out());
    osc.start(t);
    osc.stop(t + 0.16);
  }

  explode(): void {
    const ctx = this.ensure();
    const t = ctx.currentTime;
    const dur = 0.32;
    const noise = ctx.createBufferSource();
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    noise.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(900, t);
    filter.frequency.exponentialRampToValueAtTime(120, t + dur);
    const gain = ctx.createGain();
    this.env(gain, t, 0.28, 0.004, dur);
    noise.connect(filter).connect(gain).connect(this.out());
    noise.start(t);

    const thump = ctx.createOscillator();
    const tg = ctx.createGain();
    thump.type = "sine";
    thump.frequency.setValueAtTime(90, t);
    thump.frequency.exponentialRampToValueAtTime(32, t + 0.2);
    this.env(tg, t, 0.22, 0.003, 0.22);
    thump.connect(tg).connect(this.out());
    thump.start(t);
    thump.stop(t + 0.24);
  }

  correct(): void {
    const ctx = this.ensure();
    const t = ctx.currentTime;
    for (const [i, freq] of [523.25, 784.0, 1046.5].entries()) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      this.env(gain, t + i * 0.045, 0.08, 0.01, 0.12);
      osc.connect(gain).connect(this.out());
      osc.start(t + i * 0.045);
      osc.stop(t + i * 0.045 + 0.16);
    }
  }

  wrong(): void {
    const ctx = this.ensure();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.linearRampToValueAtTime(90, t + 0.18);
    this.env(gain, t, 0.09, 0.004, 0.2);
    osc.connect(gain).connect(this.out());
    osc.start(t);
    osc.stop(t + 0.22);
  }

  alarm(): void {
    const ctx = this.ensure();
    const t = ctx.currentTime;
    for (let i = 0; i < 2; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.value = 420;
      this.env(gain, t + i * 0.14, 0.07, 0.01, 0.1);
      osc.connect(gain).connect(this.out());
      osc.start(t + i * 0.14);
      osc.stop(t + i * 0.14 + 0.12);
    }
  }

  damage(): void {
    this.wrong();
    const ctx = this.ensure();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(240, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.22);
    this.env(gain, t, 0.16, 0.004, 0.22);
    osc.connect(gain).connect(this.out());
    osc.start(t);
    osc.stop(t + 0.26);
  }

  thunder(): void {
    this.explode();
    const ctx = this.ensure();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(48, t);
    osc.frequency.exponentialRampToValueAtTime(28, t + 0.7);
    this.env(gain, t, 0.12, 0.02, 0.7);
    osc.connect(gain).connect(this.out());
    osc.start(t);
    osc.stop(t + 0.8);
  }

  explosion(): void {
    this.explode();
  }

  combo(level: number): void {
    const ctx = this.ensure();
    const t = ctx.currentTime;
    const base = 392 * (1 + Math.min(level, 8) * 0.03);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(base, t);
    osc.frequency.exponentialRampToValueAtTime(base * 2, t + 0.16);
    this.env(gain, t, 0.08, 0.01, 0.18);
    osc.connect(gain).connect(this.out());
    osc.start(t);
    osc.stop(t + 0.22);
  }

  startAmbient(): void {
    if (this.ambient) return;
    const ctx = this.ensure();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 280;
    filter.Q.value = 8;
    const mix = ctx.createGain();
    mix.gain.value = 0.11;
    filter.connect(mix).connect(this.out());

    const oscA = ctx.createOscillator();
    const oscB = ctx.createOscillator();
    oscA.type = "sawtooth";
    oscB.type = "sawtooth";
    oscA.frequency.value = 55;
    oscB.frequency.value = 55.4;
    oscA.connect(filter);
    oscB.connect(filter);
    oscA.start();
    oscB.start();

    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.07;
    lfoGain.gain.value = 180;
    lfo.connect(lfoGain).connect(filter.frequency);
    lfo.start();

    const rain = ctx.createBufferSource();
    const nbuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const nd = nbuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
    rain.buffer = nbuf;
    rain.loop = true;
    const rainFilter = ctx.createBiquadFilter();
    rainFilter.type = "highpass";
    rainFilter.frequency.value = 1800;
    const rainGain = ctx.createGain();
    rainGain.gain.value = 0.03;
    rain.connect(rainFilter).connect(rainGain).connect(this.out());
    rain.start();

    this.ambient = {
      stop: () => {
        oscA.stop();
        oscB.stop();
        lfo.stop();
        rain.stop();
      },
    };
  }
}
